/**
 * Shared packing types for Empaque (GRANEL / PALLET).
 * Coordinates: x = width, y = height (up), z = length (front→back).
 */

/**
 * @typedef {Object} Orientation
 * @property {number} boxW
 * @property {number} boxH
 * @property {number} boxD
 * @property {string} label
 * @property {number} nx
 * @property {number} ny
 * @property {number} nz
 */

/**
 * @typedef {Object} PlacedBox
 * @property {number} x
 * @property {number} y
 * @property {number} z
 * @property {number} w
 * @property {number} h
 * @property {number} d
 * @property {number} [col]
 * @property {number} [level]
 * @property {number} [row]
 * @property {"master"|"pallet"} kind
 * @property {number} [containerIndex]
 */

/**
 * @typedef {Object} StepInfo
 * @property {number} startCol
 * @property {number} numCols
 * @property {number} heightBoxes
 */

/**
 * @typedef {Object} ContainerPackResult
 * @property {number} containerIndex
 * @property {number} contL
 * @property {number} contW
 * @property {number} contH
 * @property {Orientation} orientation
 * @property {number} quantity
 * @property {number} capacity
 * @property {PlacedBox[]} boxes
 * @property {number[]} columnHeights
 * @property {StepInfo[]} steps
 * @property {[number, number, number, number]|null} diagonal
 */

/**
 * @typedef {Object} PackPlan
 * @property {Orientation|null} orientation
 * @property {ContainerPackResult[]} containers
 * @property {number} totalQuantity
 * @property {number} totalCapacity
 */

/**
 * @param {Partial<Orientation>} o
 * @returns {Orientation}
 */
export function createOrientation(o) {
  return {
    boxW: o.boxW ?? 0,
    boxH: o.boxH ?? 0,
    boxD: o.boxD ?? 0,
    label: o.label ?? "",
    nx: o.nx ?? 0,
    ny: o.ny ?? 0,
    nz: o.nz ?? 0,
  };
}

/**
 * @param {Orientation} o
 * @returns {number}
 */
export function orientationCapacity(o) {
  return o.nx * o.ny * o.nz;
}

/**
 * Parse EXPORT_CONTAINER_TYPE Props → packing dims.
 * Props: { width, height, depth } → contW, contH, contL (depth = length).
 * @param {object|string|null|undefined} props
 * @returns {{ contL: number, contW: number, contH: number }|null}
 */
export function containerDimsFromProps(props) {
  let parsed = props;
  if (typeof props === "string") {
    try {
      parsed = JSON.parse(props);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const width = Number(parsed.width ?? parsed.Width);
  const height = Number(parsed.height ?? parsed.Height);
  const depth = Number(parsed.depth ?? parsed.Depth);
  if (![width, height, depth].every((n) => Number.isFinite(n) && n > 0)) {
    return null;
  }
  return { contL: depth, contW: width, contH: height };
}
